import { DateTime } from 'luxon';
// `Model` from its own module, not the `@/models` barrel — see the note in `index.ts`.
import { Model } from './model';
import { DateMode, FormFieldType, type Coordinates, FormFieldChoice,
  type FormModel, type NetworkLocationData,
  type FormField} from '@/models';
import { getInitialMatrixData } from '@/components/data/entry/items/MatrixHelper';
import type { DrillData } from '@/components/forms/form_builder/FormFieldConfigHelper';
import { getLocalTimezoneDetails, getTimeFromSeconds, getUTCJSDate } from '@/utils';
import type { Part11Signature } from './part11';

export const OTHER_CHOICE_CODE = 'xxxx-OTHER_CHOICE-xxxx';

/**
 * Identifies the entry to load and submit. The entry store normally derives this from the current route, but hosts that
 * render the entry outside its own route (e.g. the pipeline's entry dialog) supply it explicitly instead.
 */
export interface EntryContext {
  formIdentifier: string;
  entryIdentifier?: string;
  caseIdentifier?: string;
  token?: string;
  /**
   * A field to open the form at, named the way the cell card named it: by label, since the case list
   * is the one endpoint that reports unanswered fields by `field.label` rather than by
   * `field.identifier`.
   */
  fieldName?: string;
  /**
   * Open in edit mode rather than preview, where the entry would offer its Edit button anyway.
   * Asked for by a cell card's requirement row alongside `fieldName`.
   */
  edit?: boolean;
  /**
   * A new entry's source: the saved entry whose answers it starts from, which the entry list's Copy
   * puts in the route as `?copyFrom=` (ADR 0012).
   */
  copyFrom?: string;
}

export interface NetworkEntries {
  entries: NetworkEntry[],
  entriesCount: number
}

export interface NetworkEntry {
  identifier: string;
  formIdentifier: string;
  formTitle?: string;
  caseIdentifier: string;
  caseLabel?: string | null;
  creatorNames: string;
  responderName?: string | null;
  creatorAvatar?: string | null;
  responderAvatar?: string | null;
  lockedAt: string;
  createdAt: string;
  updatedAt: string;

  archivedAt?: string;
  items?: Record<string, string | number>
  completion?: NetworkEntryCompletion;
}

/**
 * How much of an entry's required work is done, as the entry carries it.
 *
 * `applies: false` is a fact, not a gap: the study configures no completion logic, or the entry's
 * form is not one the study holds, so there is nothing to measure it against. A form the study does
 * hold but which declares none of the fields the logic measures arrives as
 * `{ applies: true, total: 0, completed: 0 }` — measured, with nothing in it to measure.
 *
 * It used to arrive as `undefined`, which also stood for
 * "logic is on and this cell has nothing in it yet" — two answers that mean opposite things read
 * off one absent field. `PipelineCell` told them apart by minting `{ total: 100, completed: 0 }`
 * for the second and testing for that exact pair in four places.
 */
export type EntryCompletion =
  | { applies: false }
  | { applies: true; total: number; completed: number; unanswered: string[] };

/**
 * Completion as the wire carries it, in either of the two shapes in service.
 *
 * The paginated case list in `pipeline` mode sends the first. Every other endpoint that computes
 * completion — the entry read, the entry list, the public API — still sends the second, and the
 * client is deployed separately from the API, so a client that only understood the new shape would
 * show every entry as unmeasured until the API caught up. `normalizeEntryCompletion` takes either.
 */
export type NetworkEntryCompletion =
  | { applies: false }
  | { applies: true; total: number; completed: number; unanswered?: string[] }
  | { totalRequiredFields: number; answeredRequiredFields: number; unansweredFields?: string[] };

const COMPLETION_DOES_NOT_APPLY: EntryCompletion = { applies: false };

/** Reads either wire shape, and an absent one, into the shape the app works in. */
export function normalizeEntryCompletion(raw?: NetworkEntryCompletion): EntryCompletion {
  if (!raw) {
    return COMPLETION_DOES_NOT_APPLY;
  }

  if ('applies' in raw) {
    return raw.applies ?
      { applies: true, total: raw.total, completed: raw.completed, unanswered: raw.unanswered ?? [] } :
      COMPLETION_DOES_NOT_APPLY;
  }

  return {
    applies: true,
    total: raw.totalRequiredFields,
    completed: raw.answeredRequiredFields,
    unanswered: raw.unansweredFields ?? []
  };
}

/** Whether nothing the study measures is outstanding on this entry. */
export function isEntryComplete(completion: EntryCompletion): boolean {
  return !completion.applies || completion.total === completion.completed;
}

interface IEntry {
  identifier: string;
  formIdetifier: string;
  caseIdentifier?: string;
  formTitle?: string;
  caseLabel?: string;
  creatorNames: string;
  creatorAvatar: string | null;
  lockedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;

  archivedAt?: Date;
  items?: Record<string, string | number>;
  completion: EntryCompletion;
}

/**
 * Reads a saved drilldown back into its path. The answer is the picked labels joined with commas, and a label can hold
 * a comma, so splitting on each one lost that level and every level below it. Instead each level tries every child of
 * the pick above it whose label starts the rest of the answer: a label can repeat under different parents, so it is
 * looked up there alone. A reading that uses the whole answer wins, then the one with more levels, and a label it
 * can't find ends the path there rather than shifting the rest up.
 */
const readDrillPath = (
  levels: readonly { data: DrillData[] }[] | undefined,
  saved: string,
  index = 0,
  parent?: DrillData
): { path: DrillData[]; whole: boolean } => {
  let best: { path: DrillData[]; whole: boolean } = { path: [], whole: saved === '' };
  for (const option of levels?.[index]?.data ?? []) {
    const underParent = index === 0 || option.parentId === parent?.id;
    const startsAnswer = saved === option.label || saved.startsWith(`${option.label},`);
    if (!underParent || !startsAnswer) {
      continue;
    }
    const below = readDrillPath(levels, saved.slice(option.label.length + 1), index + 1, option);
    const reading = { path: [option, ...below.path], whole: below.whole };
    if ((reading.whole && !best.whole) || (reading.whole === best.whole && reading.path.length > best.path.length)) {
      best = reading;
    }
  }
  return best;
};

/** What a field's `<id>_data` holds: a datetime's zone, and on a field the reader overrode the override mark (ENG-9047). */
interface StoredFieldData {
  timezone?: string;
  overrideCalculatedValue?: boolean;
}

/** Reads `<id>_data`. A blank or unreadable value reads as nothing, so a bad key costs the mark rather than the entry. */
const readDataKey = (stored: string | number | undefined): StoredFieldData | undefined => {
  if (typeof stored !== 'string' || stored === '') {
    return undefined;
  }
  try {
    const data = JSON.parse(stored);
    return data && typeof data === 'object' ? data : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Whether a stored answer is one: ADR 0009's rule, read off the saved value as the server reads it. `isAnswered` reads
 * the loaded field instead, and importing `fieldAnswer.ts` here closes a module cycle that breaks loading the store
 * through `@/router`.
 */
const isSavedAnswer = (value: string | number | undefined) => value !== undefined && value !== null && value !== '';

export class EntryModel extends Model {
  identifier: string;
  formIdentifier: string;
  caseIdentifier?: string;
  formTitle?: string;
  items?: Record<string, string | number>;
  caseLabel?: string;
  creatorNames: string;
  creatorAvatar: string | null;
  lockedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;

  archivedAt?: Date;
  completion: EntryCompletion;

  constructor(entry: IEntry) {
    super();
    this.identifier = entry.identifier;
    this.formIdentifier = entry.formIdetifier;
    this.caseIdentifier = entry.caseIdentifier;
    this.formTitle = entry.formTitle;
    this.caseLabel = entry.caseLabel;
    this.creatorNames = entry.creatorNames;
    this.creatorAvatar = entry.creatorAvatar;
    this.lockedAt = entry.lockedAt;
    this.createdAt = entry.createdAt;
    this.updatedAt = entry.updatedAt;
    this.archivedAt = entry.archivedAt;
    this.items = entry.items;
    this.completion = entry.completion;
  }

  /**
   * The row as it arrives, with nothing looked up beside it.
   *
   * `caseLabel` comes down joined to the row. It used to be found by scanning a list of every case
   * in the study, once per row — which meant the list could not be read until that whole list had
   * been, on a study of any size.
   */
  static mapFromBackend(networkEntry: NetworkEntry) {
    return new EntryModel({
      identifier: networkEntry.identifier,
      formIdetifier: networkEntry.formIdentifier,
      caseIdentifier: networkEntry.caseIdentifier,
      formTitle: networkEntry.formTitle,
      caseLabel: networkEntry.caseLabel ?? undefined,
      items: networkEntry.items,
      creatorNames: networkEntry.creatorNames,
      creatorAvatar: networkEntry.creatorAvatar ?? networkEntry.responderAvatar ?? '',
      lockedAt: networkEntry.lockedAt ? DateTime.fromISO(networkEntry.lockedAt).toJSDate() : null,
      createdAt: DateTime.fromISO(networkEntry.createdAt).toJSDate(),
      updatedAt: DateTime.fromISO(networkEntry.updatedAt).toJSDate(),
      archivedAt: networkEntry.archivedAt ? DateTime.fromISO(networkEntry.archivedAt).toJSDate() : undefined,
      completion: normalizeEntryCompletion(networkEntry.completion)
    });
  }

  static mapWithItems(networkEntry: NetworkEntry, form?: FormModel) {

    EntryModel.mapWithFormItems(form?.items, networkEntry.items);

    return new EntryModel({
      identifier: networkEntry.identifier,
      formIdetifier: networkEntry.formIdentifier,
      creatorNames: networkEntry.creatorNames ?? networkEntry.responderName,
      creatorAvatar: networkEntry.creatorAvatar ?? networkEntry.responderAvatar ?? '',
      lockedAt: networkEntry.lockedAt ? DateTime.fromISO(networkEntry.lockedAt).toJSDate() : null,
      createdAt: DateTime.fromISO(networkEntry.createdAt).toJSDate(),
      updatedAt: DateTime.fromISO(networkEntry.updatedAt).toJSDate(),
      archivedAt: networkEntry.archivedAt ? DateTime.fromISO(networkEntry.archivedAt).toJSDate() : undefined,
      completion: normalizeEntryCompletion(networkEntry.completion)
    });
  }

  static mapWithFormItems(formItems?: FormField[], entryItems?: Record<string, string | number>) {
    formItems?.forEach(formField => {
      const value = entryItems![formField.identifier];
      const data = readDataKey(entryItems![`${formField.identifier}_data`]);

      switch (formField.type) {
        // A barcode is a code, so a number stored for one loads as its digits: the answer table trims it as a string.
        case FormFieldType.BARCODE:
        case FormFieldType.TEXT:
          if (value) {
            formField.value = `${value}`;
          } else {
            formField.value = '';
          }

          break;

        case FormFieldType.NUMBER:
          if (formField.options.range) {
            if (value) {
              formField.value = `${value}`;
            } else {
              formField.value = '';
            }
          } else {
            formField.numberValue = value ? parseFloat(`${value}`) : null;
          }
          break;

        case FormFieldType.CHOICE:
          if (formField.options.maximumChoicesCount == 1) {
            if (value) {
              formField.choiceValue = formField.options.choices?.find(choice => choice.value == value);
            }
            if (formField.options.allowOther) {
              formField.otherChoiceValue = {
                id: 99999999,
                label: 'Other',
                value: formField.choiceValue ? '-1' : value ? OTHER_CHOICE_CODE : '-1',
                otherValue: !formField.choiceValue ? `${value}` : ''
              };
            }
            if (!formField.choiceValue) {
              formField.choiceValue = FormFieldChoice.createEmpty();
            }
          } else {
            const valueChoices = `${value}`?.split(',');
            if (valueChoices) {
              valueChoices.forEach(choiceValue => {
                for (const choice of formField.options.choices!) {
                  if (choice.value == choiceValue) {
                    choice.checked = true;
                    break;
                  }
                }
              });
            }

            for (const choice of formField.options.choices!) {
              if (choice.checked === undefined) {
                choice.checked = false;
              }
            }

            if (formField.options.allowOther) {
              let otherValue = '';
              if (valueChoices) {
                for (const valueChoice of valueChoices) {
                  let hasOtherValue = true;
                  for (const choice of formField.options.choices!) {
                    if (`${choice.value}` === valueChoice) {
                      hasOtherValue = false;
                      break;
                    }
                  }
                  if (hasOtherValue) {
                    otherValue = valueChoice;
                    break;
                  }
                }
              }

              formField.otherChoiceValue = {
                id: 99999999,
                label: 'Other',
                checked: otherValue !== '',
                otherValue: otherValue,
                value: otherValue ? OTHER_CHOICE_CODE : '-1'
              };
            }
          }
          break;

        case FormFieldType.DROPDOWN:
          if (formField.options.maximumChoicesCount == 1) {
            formField.choiceValue = formField.options.choices?.find(choice => choice.value == value);
          } else {
            const multipleChoices: FormFieldChoice[] = [];

            if (value === undefined) {
              break;
            }

            const valueChoices = `${value}`.split(',');
            if (valueChoices) {
              valueChoices.forEach(choiceValue => {
                for (const choice of formField.options.choices!) {
                  if (choice.value == choiceValue) {
                    multipleChoices.push(choice);
                    break;
                  }
                }
              });
            }

            formField.choiceValue = multipleChoices;
          }
          break;

        case FormFieldType.DATE:
          if (value) {
            if (formField.options.mode === DateMode.DATE_TIME) {
              /** When the absolute date option is selected, the timestamp is stored as the number of seconds since the Unix epoch (UTC seconds).
               * This means the time is fixed and should display the exact same moment, regardless of which timezone it’s viewed in.
               * Importantly, the display does NOT convert this time to the viewer’s local timezone. Instead, it displays the original collection
               * time along with the timezone information it was recorded in. For example, if the data was collected as ‘18 Sept 2025, 12:50 PM’,
               * it will always show ‘18 Sept 2025, 12:50 PM’ regardless of the viewer’s current timezone. */
              if (formField.options.absoluteDate) {
                formField.dateValue = getUTCJSDate(parseInt(value as string, 10));
              } else {
                formField.dateValue = DateTime.fromSeconds(parseInt(value as string)).toJSDate();
              }

              // A `_data` with no zone holds only the override mark, which is read below for every type.
              formField.timezoneRecorded = data?.timezone ? { ...data, timezone: data.timezone } : getLocalTimezoneDetails();
            } else if (formField.options.mode === DateMode.DATE) {
              formField.dateValue = getUTCJSDate(parseInt(value as string, 10));
            } else if (formField.options.mode === DateMode.TIME) {
              formField.dateValue = getTimeFromSeconds(parseInt(value as string, 10));
            }
          }
          break;

        case FormFieldType.LOCATION:
          if (value) {
            // eslint-disable-next-line no-case-declarations
            const locationObject: NetworkLocationData = JSON.parse(value as string);

            // eslint-disable-next-line no-case-declarations
            const coordinates: Coordinates = {
              latitude: locationObject.latitude,
              longitude: locationObject.longitude,
              accuracy: locationObject.accuracy
            };

            formField.locationValue = {
              coords: coordinates,
              timestamp: locationObject.timestamp
            };
          }
          break;

        case FormFieldType.SLIDER:
          formField.numberValue = value ? parseFloat(`${value}`) : undefined;
          break;

        case FormFieldType.SIGNATURE:
          if (value) {
            formField.signatureValue = JSON.parse(value as string);
          }
          break;

        case FormFieldType.FILE:
          if (value) {
            formField.fileIds = JSON.parse(value as string);
          }
          break;

        case FormFieldType.MATRIX:
          formField.matrixValues = value ? JSON.parse(value as string) : [];
          if (formField.matrixValues && formField.matrixValues.length > 0) {
            formField.hasExistingMatrixValue = true;
          }
          formField.matrixTableRowValues = getInitialMatrixData(formField);
          break;

        case FormFieldType.DRILLDOWN:

          formField.drillValue = readDrillPath(formField.options.drillLevels, `${value}`).path;
          break;

        case FormFieldType.UNIT:
          if (value) {
            const unitValue = JSON.parse(value as string);
            formField.numberValue = unitValue.value;
            formField.value = unitValue.unit;

            if (unitValue.inequality) {
              formField.choiceValue = formField.options.choices?.find(choice => choice.value == unitValue.inequality);
            }
          } else {
            const defaultUnit = formField.options.units?.list.find(unitItem => unitItem.id === formField.options.units?.default);
            formField.value = defaultUnit?.unit;
          }
          break;

        default:
          formField.value = value as string;
          break;
      }

      // The mark goes with the answer it was saved beside (ENG-9047), so one left beside a blank answer is stale.
      formField.overrideCalculatedValue = data?.overrideCalculatedValue === true && isSavedAnswer(value) ? true : undefined;
    });
  }
}

/**
 * The `reasons` an activity row written before ENG-9108 holds in `difference.newValues`: the English
 * sentence the reason dialog sent, before the signature meaning went as a code. Nothing sends it now.
 */
export interface EntryUpdateReason {
  reason: string;
  comment?: string;
  type: 'update' | 'review'
}

export interface PostEntry {
  items?: Record<string, string | number>,
  caseUri?: string | null;
  revisionDate: number;
  uri?: string;
  studyId?: string;
  entryToken?: string;
  /** The Part 11 signature a save on a `cfrPart11` study carries (ENG-9108). */
  signature?: Part11Signature;
  /**
   * The `updatedAt` the edit was made against, as an ISO string. The API refuses an edit with a 409
   * when the entry has moved on since (ENG-9078).
   */
  expectedUpdatedAt?: string;
}

export interface UploadFile {
  filename: string;
  filetype: string;
  itemIdnetifier: string;
  size: number;
  progress?: number;
  file?: File;
}
